import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api } from "../lib/api";

const STATUS_RO = {
  received: "Primita",
  in_verification: "In verificare",
  repaired: "Reparata pe garantie",
  cancelled: "Garantie ANULATA",
  shipped: "Expediata inapoi",
};

export default function WarrantyPrintPage() {
  const { id } = useParams();
  const [w, setW] = useState(null);
  const [error, setError] = useState(false);
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("kk_token")) { setAuthed(false); return; }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (!authed) return;
    api.get("/admin/warranties").then((r) => {
      const found = r.data.find((x) => x.id === id);
      if (found) {
        setW(found);
        setTimeout(() => window.print(), 700);
      } else setError(true);
    }).catch(() => setError(true));
  }, [authed, id]);

  if (authed === false) return <Navigate to="/admin/login" replace />;
  if (authed === null) return null;
  if (error) return <div className="p-10 text-center">Garantia nu a fost gasita.</div>;
  if (!w) return <div className="p-10 text-center text-neutral-500">Se incarca...</div>;

  const receivedDay = new Date(w.received_at).toLocaleDateString("ro-RO");
  const cancelled = w.status === "cancelled" || !!w.cancel_reason;
  const repaired = w.status === "repaired" || (w.status === "shipped" && !w.cancel_reason);

  return (
    <div data-testid="warranty-print-page" className="kk-print bg-white text-black min-h-screen">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
        .kk-print { font-family: 'Outfit', sans-serif; }
        .kk-print h1, .kk-print h2 { font-family: 'Anton', sans-serif; letter-spacing: -0.02em; }
      `}</style>

      <div className="no-print fixed top-4 right-4 flex gap-2 z-50">
        <button data-testid="print-btn" onClick={() => window.print()} className="bg-[#00E5FF] text-black font-semibold px-4 py-2 text-sm uppercase tracking-wider">Printeaza</button>
        <button data-testid="close-btn" onClick={() => window.close()} className="border border-neutral-400 text-neutral-700 px-4 py-2 text-sm uppercase tracking-wider">Inchide</button>
      </div>

      <div className="max-w-3xl mx-auto p-8">
        <div className="flex items-start justify-between pb-5 border-b-2 border-black">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-[#00E5FF] flex items-center justify-center text-white font-bold text-xl">K</div>
              <div>
                <h1 className="text-2xl font-bold uppercase leading-none">TROTI-X SERVICE</h1>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#00E5FF] mt-1">Service garantii · trotinete-ieftine.ro</div>
              </div>
            </div>
            <div className="text-xs text-neutral-700 mt-3 leading-relaxed">
              Bd. Liviu Rebreanu 104A, Timisoara, Timis<br />
              Tel: 0743 110 277 · Program: L-V 9:00-17:00, Sambata 9:00-14:00
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Garantie #</div>
            <div className="text-3xl font-bold tracking-tight">{w.warranty_number}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-3">Status</div>
            <div className={`inline-block px-2 py-1 text-xs uppercase tracking-wider font-bold mt-1 text-white ${cancelled ? "bg-red-600" : "bg-[#00E5FF]"}`}>
              {STATUS_RO[w.status] || w.status}
            </div>
          </div>
        </div>

        <h2 className="text-4xl uppercase font-bold mt-8 leading-none">
          Fisa de <span className="text-[#00E5FF]">constatare garantie</span>
        </h2>
        <div className="text-sm text-neutral-600 mt-2">Data intrarii in service: <span className="font-medium text-black">{receivedDay}</span></div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="border-2 border-black p-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">AWB intrare</div>
            <div className="text-2xl font-bold font-mono mt-1">{w.awb_in}</div>
          </div>
          <div className="border-2 border-black p-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">AWB retur</div>
            <div className="text-2xl font-bold font-mono mt-1">{w.awb_out || "—"}</div>
            {w.shipped_at && <div className="text-[10px] text-neutral-500 mt-1">Expediata: {new Date(w.shipped_at).toLocaleDateString("ro-RO")}</div>}
          </div>
        </div>

        <Section title="Client">
          <Row label="Nume" value={w.customer_name} big />
          <Row label="Telefon" value={w.customer_phone || "-"} />
        </Section>

        <Section title="Trotineta">
          <Row label="Model" value={w.scooter_model} big />
          <Row label="Numar serie" value={w.serial_number || "-"} />
        </Section>

        <Section title="Problema reclamata de client">
          <div className="bg-neutral-100 border-l-4 border-[#00E5FF] p-4 text-sm leading-relaxed whitespace-pre-wrap min-h-[80px]">
            {w.issue_description}
          </div>
        </Section>

        <Section title="Verdict tehnic">
          {cancelled ? (
            <div className="border-2 border-red-600 p-4 bg-red-50">
              <div className="text-red-600 font-bold uppercase text-lg">✗ GARANTIE ANULATA</div>
              <div className="text-sm mt-2"><strong>Motiv:</strong> {w.cancel_reason}</div>
            </div>
          ) : repaired ? (
            <div className="border-2 border-green-600 p-4 bg-green-50">
              <div className="text-green-700 font-bold uppercase text-lg">✓ ACCEPTATA — Reparata pe garantie</div>
            </div>
          ) : (
            <div className="border-2 border-neutral-400 p-4 bg-neutral-50">
              <div className="text-neutral-600 font-bold uppercase text-lg">In curs de verificare</div>
            </div>
          )}
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Constatare tehnica</div>
            {w.verdict_notes ? (
              <div className="text-sm leading-relaxed whitespace-pre-wrap border border-neutral-300 p-3 min-h-[60px]">{w.verdict_notes}</div>
            ) : (
              <div className="space-y-3 mt-2">
                {[1, 2, 3, 4].map((i) => <div key={i} className="border-b border-neutral-400 h-6" />)}
              </div>
            )}
          </div>
        </Section>

        <div className="grid grid-cols-2 gap-12 mt-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-600 mb-12">Tehnician (nume + semnatura)</div>
            <div className="border-t border-black pt-2">
              <div className="font-medium">TROTI-X SERVICE</div>
              <div className="text-[10px] text-neutral-500">Data: {new Date().toLocaleDateString("ro-RO")}</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-600 mb-12">Partener (trotinete-ieftine.ro)</div>
            <div className="border-t border-black pt-2">
              <div className="text-[10px] text-neutral-500">Data: ______________</div>
            </div>
          </div>
        </div>

        <div className="mt-10 text-center text-[10px] uppercase tracking-widest text-neutral-500">
          Fisa constatare garantie · {w.warranty_number} · Partener trotinete-ieftine.ro
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-6">
    <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-2">// {title}</div>
    {children}
  </div>
);

const Row = ({ label, value, big }) => (
  <div className="flex items-baseline gap-4 py-2 border-b border-neutral-200">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 w-32 flex-shrink-0">{label}</div>
    <div className={`font-medium ${big ? "text-xl" : "text-base"}`}>{value}</div>
  </div>
);
